import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alertwhale/ui'
import { RefreshCw, Loader2, AlertCircle } from 'lucide-react'
import { useLiveSession, useUpdateLiveSession } from '@/hooks/useLiveSessions'
import { toast } from 'sonner'

interface UpdateLiveSessionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string
}

export function UpdateLiveSessionDialog({ open, onOpenChange, sessionId }: UpdateLiveSessionDialogProps) {
  const { data: session, isLoading } = useLiveSession(sessionId)
  const updateSession = useUpdateLiveSession(sessionId)

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    progress: 0,
    status: 'active' as 'active' | 'paused' | 'ended',
  })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (session) {
      setFormData({
        title: session.title,
        subtitle: session.subtitle || '',
        progress: session.progress,
        status: session.status,
      })
    }
  }, [session])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.title.trim()) {
      setError('Title is required')
      return
    }

    try {
      await updateSession.mutateAsync({
        title: formData.title,
        subtitle: formData.subtitle || null,
        progress: formData.progress,
        status: formData.status,
      })
      toast.success('Session updated successfully')
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update session')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Update Live Session</DialogTitle>
          <DialogDescription>
            Update the session details and progress
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 pt-2">
            {error && (
              <div className="flex items-center gap-3 rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-red-400">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Session title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subtitle">Subtitle</Label>
              <Input
                id="subtitle"
                placeholder="Additional info (optional)"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                maxLength={200}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="progress">Progress</Label>
                <span className="text-sm font-semibold text-cyan-400">
                  {Math.round(formData.progress * 100)}%
                </span>
              </div>
              <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-800">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-400 transition-all duration-200"
                  style={{ width: `${formData.progress * 100}%` }}
                />
                <input
                  type="range"
                  id="progress"
                  value={formData.progress * 100}
                  onChange={(e) => setFormData({ ...formData, progress: Number(e.target.value) / 100 })}
                  min={0}
                  max={100}
                  step={1}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value: 'active' | 'paused' | 'ended') =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="ended">Ended</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateSession.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateSession.isPending}
              >
                {updateSession.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Update Session
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
